import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from './SEO';
import useIsMobile from '../hooks/useIsMobile';

const servicePartners = [
  { name: 'Bailey-Hill', location: 'Eugene, OR', type: 'Service Partner', site: 'https://www.baileyhill-llc.com/', img: '/images/logos/baileyhill-5423d88f.png' },
  { name: 'OSSC', location: '', type: 'Service Partner', site: 'https://www.ossc-inc.com/', img: '/images/logos/ossc-a89cf94f.png' },
  { name: 'Articul8 Solutions', location: '', type: 'Service Partner', site: 'https://articul8solutions.com/', img: '/images/logos/articul8-c130f9f0.png' },
  { name: 'Seamless POC', location: '', type: 'Service Partner', site: 'https://www.seamlesspoc.com/', img: '/images/logos/seamlesspoc-d2f0fd14.png' },
];

const solutionsProviders = [
  { name: 'Touchpoint Medical', location: 'Nashville, TN', type: 'Medical Equipment Partner', site: 'https://www.touchpointmed.com', img: '/images/logos/touchpoint-27d49df8.png', imgStyle: undefined },
  { name: 'Lund-Data Cart', location: 'Burnsville, MN', type: 'Medical Cart Partner', site: 'https://data-carts.com/', img: '/images/logos/lunddata-45d707ca.png', imgStyle: undefined },
  { name: 'Tangent Computers', location: 'Milpitas, CA', type: 'Healthcare Computing Partner', site: 'https://www.tangent.com/', img: '/images/logos/tangent-b8e8da06.png', imgStyle: undefined },
  { name: 'GCX Healthcare Solutions', location: 'Petaluma, CA', type: 'Medical Mounting Solutions Partner', site: 'https://www.gcx.com', img: '/images/logos/gcx-bfd3b940.png', imgStyle: undefined },
  { name: 'Altus Healthcare Technology', location: 'Grand Rapids, MI', type: 'Medical Workstation & Cart Partner', site: 'https://www.altus-inc.com/', img: '/images/logos/altus-0199eb6d.png', imgStyle: undefined },
  { name: 'Capsa Healthcare', location: 'Columbus, OH', type: 'Healthcare Workflow & Cart Solutions Partner', site: 'https://www.capsahealthcare.com', img: '/images/logos/capsa-b243e1e6.png', imgStyle: undefined },
  { name: 'Strongarm Healthcare', location: 'Fort Myers, FL', type: 'Healthcare Mounting & Mobility Partner', site: 'https://strongarmhealthcare.com/', img: '/images/logos/strongarm-0bcc9009.png', imgStyle: undefined },
  { name: 'Amico', location: '', type: 'Healthcare Infrastructure Partner', site: 'https://www.amico.com/', img: '/images/logos/amico-978d7a0c.png', imgStyle: { height: '120px' } as React.CSSProperties },
  { name: 'CCI Group', location: '', type: 'Health Tech Partner', site: 'https://ccigroup.com/home', img: '/images/logos/cci-675b19d5.png', imgStyle: undefined },
  { name: 'Paladin', location: '', type: 'Healthcare Solutions Partner', site: 'https://www.paladinhc.com/', img: '/images/logos/paladin-1c4f2c08.png', imgStyle: undefined },
  { name: 'Ergotron', location: 'Saint Paul, MN', type: 'Healthcare Mounting & Mobility Solutions', site: 'https://www.ergotron.com/en-us/markets/healthcare', img: '/images/logos/ergotron-4269123e.png', imgStyle: undefined },
  { name: 'ICW', location: '', type: 'Medical Mounting Systems', site: 'https://www.touchpointmed.com/products/mounting-systems?icw', img: '/images/logos/icw-7aa37966.png', imgStyle: undefined },
];

const Products: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const products = [
    {
      id: 1,
      name: 'Hospital Workstations',
      description: 'Fixed and adjustable workstations designed for clinical environments — built for durability, infection control, and seamless integration into nursing stations and patient rooms.',
      features: ['Height-adjustable surfaces', 'Cable management', 'Antimicrobial materials', 'Monitor & peripheral mounting', 'ADA & JCO compliant installations'],
      image: '/images/products/hospital-workstation-2c306e1c.jpg',
      galleryCategory: 'workstations',
    },
    {
      id: 2,
      name: 'Patient Monitors',
      description: 'Mounting and integration solutions for patient monitors across all major brands — from bedside roller stands and wall mounts to nurse monitoring stations and anesthesia cart configurations.',
      features: ['Compatible with all major brands', 'Nurse & monitoring station setups', 'Roller, wall & anesthesia cart options', 'Integrated cable management', 'Height-adjustable solutions'],
      image: '/images/products/patient-monitors-1544d603.jpg',
      galleryCategory: 'patient_monitor',
    },
    {
      id: 3,
      name: 'Mobile Workstations',
      description: 'Powered and non-powered mobile carts that bring computing and clinical tools directly to the bedside — improving workflow efficiency and reducing steps for care teams.',
      features: ['Battery-powered options', 'Lockable storage', 'Ergonomic height adjustment', 'Lightweight & maneuverable', 'Medication & supply drawers'],
      image: '/images/products/mobile-workstation-f5666fba.jpg',
      galleryCategory: 'mobile_workstation',
    },
    {
      id: 4,
      name: 'Virtual Nursing Systems',
      description: 'Integrated technology setups that support virtual and hybrid nursing models — enabling remote patient monitoring, two-way communication, and centralized oversight from any location.',
      features: ['Camera & display integration', 'Two-way audio/video', 'Centralized monitoring support', 'Scalable across units', 'Telehealth-ready configurations'],
      image: '/images/products/virtual-nursing-system-e8ad6c2d.jpg',
      imageStyle: { objectPosition: 'top' } as React.CSSProperties,
      galleryCategory: 'desk_mount_nurse_station',
    },
    {
      id: 5,
      name: 'Specialty Installations',
      description: 'Custom equipment configurations for specialized clinical environments including ORs, ICUs, procedure rooms, and imaging suites — where precision and compliance are critical.',
      features: ['OR & ICU configurations', 'Boom & pendant systems', 'Structural coordination', 'ADA & JCO compliant installations', 'Multi-vendor integration'],
      image: '/images/products/special-af6ac3d2.jpg',
      galleryCategory: 'specialty',
    },
    {
      id: 6,
      name: 'Pharmacy',
      description: 'Workstation and storage solutions purpose-built for pharmacy environments — supporting medication management, dispensing workflows, and regulatory compliance.',
      features: ['Dispensing cart integration', 'Secure medication storage', 'Compact footprint designs', 'Barcode scanner mounting', 'Workflow-optimized layouts'],
      image: '/images/products/pharmacy-f0a82680.jpg',
      galleryCategory: 'pharmacy',
    },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": products.map((product, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": product.name,
      "description": product.description,
      "image": `https://www.jvphealthtechsolutions.com${product.image}`
    }))
  };

  return (
    <>
      <SEO
        title="Healthcare Equipment & Installations"
        description="JVP Health Tech Solutions installs hospital workstations, patient monitor arms, mobile workstations, virtual nursing stations, specialty clinical setups, and pharmacy solutions nationwide."
        keywords="hospital workstation installation, patient monitor arm mounting, mobile medical cart, virtual nursing station setup, pharmacy workstation, specialty clinical installation, healthcare equipment supplier"
        url="https://www.jvphealthtechsolutions.com/products"
        structuredData={structuredData}
      />
      <div className="page" style={{paddingTop: '80px'}}>
        <div style={{background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem', textAlign: 'center'}}>
          <h1 style={{color: 'white', fontSize: isMobile ? '2rem' : '2.8rem', marginBottom: '1rem'}}>Equipment & Installation</h1>
          <p style={{fontSize: isMobile ? '1rem' : '1.2rem', maxWidth: '600px', margin: '0 auto', opacity: 0.9}}>Professional installation services for healthcare equipment and complete facility configurations</p>
        </div>

        <div style={{maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem'}}>
          <div className="products-grid">
            {products.map(product => (
              <div key={product.id} className="product-card">
                <picture>
                  <source srcSet={product.image.replace(/\.[^.]+$/, '.avif')} type="image/avif" />
                  <img src={product.image} alt={product.name} className="product-image" style={product.imageStyle} />
                </picture>
                <div className="product-content">
                  <h3>{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                  <ul className="product-features">
                    {product.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <button className="product-button" onClick={() => navigate('/contact')}>Request Quote</button>
                    <Link to="/work" state={{ fromProducts: true, category: product.galleryCategory }} onClick={() => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })} style={{ color: 'var(--color-primary)', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>See Photos →</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Literature */}
        {/* <div style={{ background: 'var(--color-surface)', padding: isMobile ? '3rem 1.25rem' : '4rem 2rem' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <h2 style={{ color: 'var(--color-text)', fontSize: '1.75rem', marginBottom: '0.5rem' }}>Product Literature</h2>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '2.5rem' }}>Download spec sheets and product documentation from our solutions providers.</p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '1.25rem' }}>
              {[
                { file: 'CS460.pdf', title: 'CS460 Spec Sheet', description: 'Product specifications and configuration guide.' },
                { file: 'CS461.pdf', title: 'CS461 Spec Sheet', description: 'Product specifications and configuration guide.' },
                { file: 'CS462.pdf', title: 'CS462 Spec Sheet', description: 'Product specifications and configuration guide.' },
              ].map(({ file, title, description }) => (
                <a
                  key={file}
                  href={`/images/literature/${file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', background: 'white', border: '1px solid var(--color-border)', borderRadius: '12px', padding: '1.5rem', textDecoration: 'none', transition: 'box-shadow 0.2s, transform 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                >
                  <div style={{ background: 'var(--color-primary-tint)', borderRadius: '10px', padding: '0.75rem', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="12" y1="18" x2="12" y2="12" />
                      <line x1="9" y1="15" x2="15" y2="15" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: 'var(--color-text)', fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.25rem' }}>{title}</div>
                    <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', lineHeight: 1.5, marginBottom: '0.75rem' }}>{description}</div>
                    <div style={{ color: 'var(--color-primary)', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download PDF
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div> */}

        {/* Solutions Providers + Service Partners */}
        <div style={{ background: 'var(--color-surface)', padding: '4rem 2rem' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <h2 style={{ color: 'var(--color-text)', fontSize: '1.75rem', marginBottom: '0.5rem' }}>Our Solutions Providers</h2>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '2.5rem' }}>We work with industry-leading solutions providers to deliver, install, and service the best healthcare equipment.</p>
            <div className="ow-customers-grid" style={{ marginBottom: '3.5rem' }}>
              {solutionsProviders.map((p, i) => (
                <a key={i} href={p.site} target="_blank" rel="noopener noreferrer" className="ow-logo-card">
                  <div className="ow-logo-img-wrap">
                    <picture>
                      <source srcSet={p.img.replace(/\.[^.]+$/, '.avif')} type="image/avif" />
                      <img src={p.img} alt={p.name} className="ow-logo-img" style={p.imgStyle} />
                    </picture>
                  </div>
                </a>
              ))}
            </div>
            <h2 style={{ color: 'var(--color-text)', fontSize: '1.75rem', marginBottom: '0.5rem' }}>Service Partners</h2>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '2.5rem' }}>Organizations we partner with to deliver and support our services across the country.</p>
            <div className="ow-customers-grid">
              {servicePartners.map((p, i) => (
                <a key={i} href={p.site} target="_blank" rel="noopener noreferrer" className="ow-logo-card">
                {/* <a key={i} href={p.site} target="_blank" rel="noopener noreferrer" className="ow-logo-card ow-logo-card-dark"> */}
                  <div className="ow-logo-img-wrap">
                    <picture>
                      <source srcSet={p.img.replace(/\.[^.]+$/, '.avif')} type="image/avif" />
                      <img src={p.img} alt={p.name} className="ow-logo-img" />
                    </picture>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Products;
